import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, isTokenValid } from '../api';
import { resolveRedirect } from '../lib/redirect';
import { formatLocalPhone, toApiPhone } from '../lib/phone';
import { isAdminRole, isProviderRole, normalizeRole } from '../lib/roles';

// Kirish maydoni — backend (POST /api/auth/login) `users.login` ustuni bo'yicha qidiradi va
// u yerda email saqlanadi, shuning uchun foydalanuvchiga "Email" deb ko'rsatamiz.
// So'rov kaliti `login` bo'lib qoladi (backend `email` kalitini o'qimaydi).
const TEXT = {
  emailLabel: 'Email',
  emailPlaceholder: 'Email kiriting…',
  emptyFields: "Email va parol bo'sh bo'lishi mumkin emas",
  badCredentials: "Email yoki parol noto'g'ri",
};

// Telefon orqali kirish (OTP) — mobil ilova bilan bir xil endpointlar:
//   POST /api/auth/send-code   { phone }        → 200 { success, expiresIn, retryAfter } | 429 { message, retryAfter }
//   POST /api/auth/verify-code { phone, code }  → 200 { exists:true, token, role, user } | { exists:false, otpToken }
const OTP_LENGTH = 6;
const DEFAULT_RESEND_SEC = 60;
const PHONE_TEXT = {
  badPhone: "Telefon raqamini 9 raqam (XX XXX XX XX) ko'rinishida kiriting",
  badCode: `${OTP_LENGTH} xonali kodni kiriting`,
  invalidCode: "Kod noto'g'ri",
  expiredCode: 'Kod muddati tugadi. Yangi kod so\'rang.',
  smsFailed: "SMS yuborib bo'lmadi. Birozdan so'ng qayta urinib ko'ring.",
  usePhoneTab: "Bu telefon raqami — SMS kod orqali kiring. Raqam 'Telefon orqali' bo'limiga ko'chirildi.",
  notRegistered: "Bu raqam ro'yxatdan o'tmagan. Usta sifatida ro'yxatdan o'tish uchun mobil ilovadan foydalaning.",
};

// '+998 94 579 00 02' / '945790002' / '998945790002' → '998945790002'; yaroqsiz bo'lsa ''.
const normalizeUzPhone = toApiPhone;

// Kutish vaqtini o'qiladigan ko'rinishga: 45 s → "0:45", 80295 s → "22 soat".
function formatWait(sec) {
  const n = Math.max(1, Math.ceil(Number(sec) || 0));
  if (n >= 3600) return `${Math.ceil(n / 3600)} soat`;
  if (n >= 120) return `${Math.ceil(n / 60)} daqiqa`;
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
}

// 429 — body.retryAfter yoki Retry-After sarlavhasi (soniya).
function retryAfterOf(res, data) {
  const fromBody = Number(data && data.retryAfter);
  if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
  const fromHeader = Number(res.headers && res.headers.get && res.headers.get('Retry-After'));
  return Number.isFinite(fromHeader) && fromHeader > 0 ? Math.ceil(fromHeader) : DEFAULT_RESEND_SEC;
}

export default function LoginPage() {
  const [mode, setMode] = useState('email'); // 'email' | 'phone'
  const [loginVal, setLoginVal] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [mobileMsg, setMobileMsg] = useState('');
  // Shown when the user was bounced here by an expired session (apiFetch 401).
  const [infoMsg] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('expired')
        ? "Sessiya muddati tugadi. Iltimos, qaytadan tizimga kiring."
        : '';
    } catch {
      return '';
    }
  });
  const [loading, setLoading] = useState(false);

  // Telefon (OTP) rejimi
  const [phoneVal, setPhoneVal] = useState('');
  const [codeVal, setCodeVal] = useState('');
  const [otpStep, setOtpStep] = useState('phone'); // 'phone' | 'code'
  const [resendIn, setResendIn] = useState(0);
  const [timerPhone, setTimerPhone] = useState(''); // taymer qaysi raqamga tegishli

  const { auth, login: saveAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get('redirect');

  // Qayta yuborish taymeri (soniyama-soniya).
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const switchMode = (next) => {
    if (next === mode || loading) return;
    setMode(next);
    setError('');
    setMobileMsg('');
  };

  // Ikkala kirish usuli ham shu yerdan o'tadi: token/role/user AuthContext (localStorage) ga
  // saqlanadi — /admin va /provider guardlari aynan shuni o'qiydi.
  const finishLogin = (data) => {
    const { token } = data || {};
    const role = normalizeRole((data && data.role) || (data && data.user && data.user.role));
    if (!token) {
      setError('Xatolik yuz berdi');
      return;
    }
    if (isAdminRole(role)) {
      saveAuth(token, role, data.user);
      navigate(resolveRedirect(redirectParam, 'admin'), { replace: true });
    } else if (isProviderRole(role)) {
      // Hizmat ko'rsatuvchi (usta) — provayder kabinetiga o'tadi.
      saveAuth(token, role, data.user);
      navigate(resolveRedirect(redirectParam, 'provider'), { replace: true });
    } else {
      setMobileMsg('Mijozlar uchun mobil ilovadan foydalaning.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMobileMsg('');

    if (!loginVal.trim() || !password.trim()) {
      setError(TEXT.emptyFields);
      return;
    }

    // Telefon raqami email maydoniga yozilgan — parol bilan kirishda bunday login yo'q (doim 401),
    // shuning uchun so'rov yubormay, telefon (OTP) rejimiga o'tkazamiz.
    const asPhone = toApiPhone(loginVal);
    if (/^\+?[\d\s()-]+$/.test(loginVal.trim()) && asPhone) {
      setPhoneVal(formatLocalPhone(loginVal));
      setMode('phone');
      setOtpStep('phone');
      setPassword('');
      setError(PHONE_TEXT.usePhoneTab);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: loginVal.trim(), password }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {}

      if (res.status === 401) {
        setError(TEXT.badCredentials);
        return;
      }

      if (!res.ok) {
        setError((data && data.message) || 'Xatolik yuz berdi');
        return;
      }

      finishLogin(data);
    } catch {
      setError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setLoading(false);
    }
  };

  // POST helper: { res, data } — tarmoq xatosi tashqarida ushlanadi.
  const postJson = async (path, body) => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await res.json();
    } catch {}
    return { res, data };
  };

  // Server xatosini tushunarli o'zbekcha xabarga aylantiradi.
  const otpErrorText = (res, data) => {
    if (res.status === 429) {
      return `Juda ko'p urinish. ${formatWait(retryAfterOf(res, data))} dan so'ng qayta urinib ko'ring.`;
    }
    const code = data && data.code;
    if (code === 'INVALID_CODE' || code === 'OTP_INVALID') return PHONE_TEXT.invalidCode;
    if (code === 'CODE_EXPIRED' || code === 'OTP_EXPIRED') return PHONE_TEXT.expiredCode;
    if (code === 'SMS_SEND_FAILED' || res.status === 502 || res.status === 503) return PHONE_TEXT.smsFailed;
    if (res.status >= 500) return "Server xatosi. Birozdan so'ng qayta urinib ko'ring.";
    return (data && data.message) || 'Xatolik yuz berdi';
  };

  const sendCode = async () => {
    setError('');
    setMobileMsg('');
    const phone = normalizeUzPhone(phoneVal);
    if (!phone) {
      setError(PHONE_TEXT.badPhone);
      return;
    }
    setLoading(true);
    try {
      const { res, data } = await postJson('/api/auth/send-code', { phone });
      setTimerPhone(phone);
      if (res.status === 429) {
        // Taymer faqat tugmani bloklaydi; foydalanuvchida allaqachon kod bo'lishi mumkin.
        setResendIn(retryAfterOf(res, data));
        setError(otpErrorText(res, data));
        return;
      }
      if (!res.ok) {
        setError(otpErrorText(res, data));
        return;
      }
      const cooldown = Number(data && (data.expiresIn ?? data.retryAfter ?? data.cooldown));
      setResendIn(Number.isFinite(cooldown) && cooldown > 0 ? Math.ceil(cooldown) : DEFAULT_RESEND_SEC);
      setCodeVal('');
      setOtpStep('code');
    } catch {
      setError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (otpStep === 'phone') {
      if (hasCooldown) {
        // Taymer ketayotgan bo'lsa qayta yubormaymiz — mavjud kodni kiritish bosqichiga o'tamiz.
        setError('');
        setOtpStep('code');
        return;
      }
      await sendCode();
      return;
    }

    setError('');
    setMobileMsg('');
    const phone = normalizeUzPhone(phoneVal);
    const code = codeVal.replace(/\D/g, '');
    if (code.length !== OTP_LENGTH) {
      setError(PHONE_TEXT.badCode);
      return;
    }
    setLoading(true);
    try {
      const { res, data } = await postJson('/api/auth/verify-code', { phone, code });
      if (!res.ok) {
        setError(otpErrorText(res, data));
        return;
      }
      if (!data || data.exists === false || !data.token) {
        setError(PHONE_TEXT.notRegistered);
        return;
      }
      finishLogin(data);
    } catch {
      setError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setLoading(false);
    }
  };

  // Shu raqamga yaqinda kod so'ralgan (yoki 429) — qayta yuborish o'rniga mavjud kodni kiritish.
  const hasCooldown = resendIn > 0 && timerPhone === normalizeUzPhone(phoneVal);

  const changePhone = () => {
    setOtpStep('phone');
    setCodeVal('');
    setError('');
  };

  const resend = async () => {
    if (resendIn > 0 || loading) return;
    await sendCode();
  };

  // Sessiya allaqachon bor (masalan /login qo'lda ochildi) — qayta kirish so'ralmaydi.
  if (auth && (isAdminRole(auth.role) || isProviderRole(auth.role)) && isTokenValid(auth.token)) {
    return <Navigate to={resolveRedirect(redirectParam, normalizeRole(auth.role))} replace />;
  }

  const errorBlock = error && (
    <div style={s.errorBox} role="alert">
      {error}
    </div>
  );
  const mobileBlock = mobileMsg && (
    <div style={s.mobileBox} role="alert">
      <strong>Kirish taqiqlangan</strong>
      <br />
      {mobileMsg}
    </div>
  );

  return (
    <div style={s.page}>
      <div className="th-login-card" style={s.card}>
        <img src="/brand-mark.png" alt="" style={s.logo} />
        <h1 style={s.title}>
          Tez<span style={s.titleAccent}>Hal</span>
        </h1>
        <p style={s.subtitle}>Tizimga kirish</p>

        {infoMsg && (
          <div style={s.infoBox} role="status">
            {infoMsg}
          </div>
        )}

        <div style={s.tabs} role="tablist" aria-label="Kirish usuli">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'email'}
            onClick={() => switchMode('email')}
            style={{ ...s.tab, ...(mode === 'email' ? s.tabActive : {}) }}
          >
            Email orqali
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'phone'}
            onClick={() => switchMode('phone')}
            style={{ ...s.tab, ...(mode === 'phone' ? s.tabActive : {}) }}
          >
            Telefon orqali
          </button>
        </div>

        {mode === 'email' ? (
          <form onSubmit={handleSubmit} noValidate>
            <div style={s.field}>
              <label style={s.label} htmlFor="login-input">{TEXT.emailLabel}</label>
              <input
                id="login-input"
                type="email"
                value={loginVal}
                onChange={(e) => setLoginVal(e.target.value)}
                className="th-field"
                style={{ ...s.input, ...(loading ? s.inputDisabled : {}) }}
                placeholder={TEXT.emailPlaceholder}
                disabled={loading}
                autoComplete="email"
                autoFocus
              />
            </div>

            <div style={s.field}>
              <label style={s.label} htmlFor="password-input">Parol</label>
              <input
                id="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="th-field"
                style={{ ...s.input, ...(loading ? s.inputDisabled : {}) }}
                placeholder="Parolni kiriting"
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            {errorBlock}
            {mobileBlock}

            <button
              type="submit"
              disabled={loading}
              className="th-btn-primary"
              style={{ ...s.btn, ...(loading ? s.btnLoading : {}) }}
            >
              {loading ? 'Yuklanmoqda...' : 'Kirish'}
            </button>
          </form>
        ) : (
          <form onSubmit={handlePhoneSubmit} noValidate>
            <div style={s.field}>
              <label style={s.label} htmlFor="phone-input">Telefon raqami</label>
              <div style={s.phoneWrap}>
              <span style={s.phonePrefix}>+998</span>
              <input
                id="phone-input"
                type="tel"
                inputMode="tel"
                value={phoneVal}
                onChange={(e) => setPhoneVal(formatLocalPhone(e.target.value))}
                onPaste={(e) => {
                  e.preventDefault();
                  setPhoneVal(formatLocalPhone(e.clipboardData.getData('text')));
                }}
                className="th-field"
                style={{ ...s.input, paddingLeft: '62px', ...(loading || otpStep === 'code' ? s.inputDisabled : {}) }}
                placeholder="90 123 45 67"
                disabled={loading || otpStep === 'code'}
                autoComplete="tel"
                autoFocus
              />
              </div>
            </div>

            {otpStep === 'code' && (
              <div style={s.field}>
                <label style={s.label} htmlFor="code-input">SMS kod</label>
                <input
                  id="code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={OTP_LENGTH}
                  value={codeVal}
                  onChange={(e) => setCodeVal(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                  className="th-field"
                  style={{ ...s.input, ...s.codeInput, ...(loading ? s.inputDisabled : {}) }}
                  placeholder={'•'.repeat(OTP_LENGTH)}
                  disabled={loading}
                  autoComplete="one-time-code"
                  autoFocus
                />
                <div style={s.codeActions}>
                  <button type="button" style={s.linkBtn} onClick={changePhone} disabled={loading}>
                    Raqamni o'zgartirish
                  </button>
                  <button
                    type="button"
                    style={{ ...s.linkBtn, ...(resendIn > 0 || loading ? s.linkBtnOff : {}) }}
                    onClick={resend}
                    disabled={resendIn > 0 || loading}
                  >
                    {resendIn > 0 ? `Qayta yuborish (${formatWait(resendIn)})` : 'Kodni qayta yuborish'}
                  </button>
                </div>
              </div>
            )}

            {errorBlock}
            {mobileBlock}

            <button
              type="submit"
              disabled={loading}
              className="th-btn-primary"
              style={{ ...s.btn, ...(loading ? s.btnLoading : {}) }}
            >
              {loading
                ? 'Yuklanmoqda...'
                : otpStep === 'phone'
                  ? (hasCooldown ? 'Kodni kiritish' : 'Kod yuborish')
                  : 'Kirish'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--color-dark-brand)',
    fontFamily: 'var(--font-family)',
    padding: '16px',
  },
  card: {
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-card)',
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  logo: {
    // Haqiqiy brend belgisi (mobil ilova ikonkasi bilan bir xil).
    width: '58px',
    height: 'auto',
    display: 'block',
    margin: '0 auto 14px',
  },
  title: {
    margin: '0 0 4px',
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--color-text)',
  },
  titleAccent: { color: 'var(--color-primary)' },
  subtitle: {
    margin: '0 0 28px',
    fontSize: '14px',
    fontWeight: '400',
    color: 'var(--color-text-muted)',
  },
  phoneWrap: { position: 'relative' },
  phonePrefix: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-text-muted)',
    pointerEvents: 'none',
  },
  field: {
    marginBottom: '16px',
    textAlign: 'left',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-text)',
  },
  input: {
    width: '100%',
    height: '48px',
    padding: '11px 14px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-input)',
    fontSize: '14px',
    color: 'var(--color-text)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: 'var(--color-surface)',
  },
  inputDisabled: {
    background: 'var(--color-surface-alt)',
    color: 'var(--color-text-muted)',
    cursor: 'not-allowed',
  },
  errorBox: {
    background: 'rgba(229, 72, 77, 0.08)',
    borderRadius: 'var(--radius-input)',
    padding: '10px 14px',
    marginBottom: '16px',
    fontSize: '13px',
    color: 'var(--color-danger)',
    textAlign: 'left',
  },
  mobileBox: {
    background: 'var(--color-primary)',
    borderRadius: 'var(--radius-input)',
    padding: '12px 14px',
    marginBottom: '16px',
    fontSize: '14px',
    color: 'var(--color-on-primary)',
    textAlign: 'left',
    lineHeight: '1.5',
  },
  infoBox: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-input)',
    padding: '11px 14px',
    marginBottom: '18px',
    fontSize: '14px',
    color: 'var(--color-text)',
    textAlign: 'left',
    lineHeight: '1.5',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    marginBottom: '20px',
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-btn)',
  },
  tab: {
    flex: 1,
    minWidth: 0,
    height: '40px',
    padding: '0 8px',
    border: 'none',
    borderRadius: 'var(--radius-input)',
    background: 'transparent',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    fontWeight: '700',
  },
  codeInput: { letterSpacing: '6px', textAlign: 'center', fontWeight: '700', fontSize: '18px' },
  codeActions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '8px',
  },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    padding: '6px 0',
    minHeight: '32px',
    fontFamily: 'var(--font-family)',
    fontSize: '13px',
    fontWeight: '700',
    color: 'var(--color-text)',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  linkBtnOff: { color: 'var(--color-text-muted)', textDecoration: 'none', cursor: 'not-allowed' },
  btn: {
    display: 'block',
    width: '100%',
    height: '48px',
    padding: '0 12px',
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    border: 'none',
    borderRadius: 'var(--radius-btn)',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'opacity 0.2s, background-color 0.15s',
    marginTop: '24px',
  },
  btnLoading: {
    opacity: '0.6',
    cursor: 'not-allowed',
  },
};
